import { Error, NewReleases, Unpublished, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { formatDate } from '~/lib/datetime';
import type { DeviceVersion__Device as Device } from './index.generated';

/* GraphQL */ `#graphql
  fragment DeviceVersion__Device on Device {
    playerVersion
    softwareVersion {
      approved
      current
      eoledAt
    }
    usesLatestVersion
  }
`;

export interface DeviceVersionProps {
  className?: string;
  device: Device;
}

export const DeviceVersion = ({ device }: DeviceVersionProps) => {
  const { t } = useTranslation();
  // Each icon is the only sign of its status, so it names itself (WCAG 1.1.1) and the tooltip
  // repeats the name for the pointer, not as a second description.
  const statusIcon = (key: string, title: string, Icon: typeof Warning, color: string) => (
    <Tooltip describeChild={false} key={key} title={title}>
      <Icon titleAccess={title} sx={{ color, marginRight: '5px' }} />
    </Tooltip>
  );
  const statusIcons = [
    !device.usesLatestVersion &&
      statusIcon(
        'not-latest',
        t('common.tooltips.new_release_available'),
        NewReleases,
        'success.main',
      ),
    device.playerVersion == null &&
      statusIcon(
        'player-version',
        t('common.tooltips.player_version_not_available'),
        Warning,
        'warning.text',
      ),
    device.softwareVersion == null &&
      statusIcon(
        'software-version',
        t('common.tooltips.software_version_not_available'),
        Warning,
        'warning.text',
      ),
    device.softwareVersion?.current === false &&
      statusIcon(
        'eoled',
        t('common.tooltips.version_end_of_life', {
          date: formatDate(device.softwareVersion.eoledAt),
        }),
        Error,
        'error.main',
      ),
    // text.secondary rather than a light grey: a status icon needs 3:1 (WCAG 1.4.11).
    device.softwareVersion?.approved === false &&
      statusIcon(
        'unapproved',
        t('common.tooltips.version_unapproved'),
        Unpublished,
        'text.secondary',
      ),
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {statusIcons}
      {device.playerVersion ?? 'N/A'}
    </div>
  );
};
